/* eslint-disable @typescript-eslint/naming-convention */
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, mergeMap, skip, take } from 'rxjs/operators';
import { faList } from '@fortawesome/free-solid-svg-icons/faList';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { InterventionStore } from '../../../store/intervention/component-store/intervention.store';
import { PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

@Component({
  selector: 'app-intervention-review',
  templateUrl: './intervention-review.component.html',
  styleUrls: ['./intervention-review.component.scss'],
  providers: [InterventionStore],
})
export class InterventionReviewComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  @Output()
  public emitHasInterventions = new EventEmitter<boolean>();

  // Icons
  faList = faList;

  public isLoading$: Observable<boolean>;

  public initialInterventions: InterventionInterface[];

  // Filter
  public filter = {
    searchFilter: '',
    // Has to be 0 to run emitHasInterventions on init
    typeOfIntervention: 0,
  };

  public isCollapse$: Observable<boolean>;

  public interventions$: Observable<InterventionInterface[]>;

  public searchText$ = new BehaviorSubject<string>('');

  public pagedInterventions$: Observable<InterventionInterface[]>;

  // Translation for study name
  public param = { intervention_name: '...' };
  public resultParam = { result_value: 0 };

  public profile$: Observable<ProfileInterface>;
  public isCollapseSubject = new BehaviorSubject<boolean>(true);

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private interventionsSubject = new BehaviorSubject<InterventionInterface[]>([]);

  private pendingReviewingInterventions$: Observable<InterventionInterface[]>;

  private pagedInterventionsSubject = new BehaviorSubject<InterventionInterface[]>([]);

  private subscriptions: Subscription[] = [];

  constructor(
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private router: Router,
    private translateService: TranslateService,
    private interventionStore: InterventionStore,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.pagedInterventions$ = this.pagedInterventionsSubject.asObservable();
    this.isCollapse$ = this.isCollapseSubject.asObservable();
    this.interventions$ = this.interventionsSubject.asObservable();
    this.pendingReviewingInterventions$ = this.interventionStore.pendingReviewingInterventions$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    this.applyInterventionFilter();

    // Subscription to language change
    this.subscriptions.push(
      this.translateService.onLangChange.subscribe(() => {
        this.applyInterventionFilter();
      }),
    );
  }

  public applyInterventionFilter(): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadInterventionList(true).subscribe(() => {
        this.isLoadingSubject.next(false);
      }),
    );
  }

  private getFormGuidanceType(typeOfIntervention: number) {
    if (typeOfIntervention === 2) {
      return 'accompanied';
    } else if (typeOfIntervention === 1) {
      return 'unaccompanied';
    } else {
      return undefined;
    }
  }

  public reloadInterventionList(setFirst?: boolean): Observable<boolean> {
    const paramGuidanceType = this.getFormGuidanceType(this.filter.typeOfIntervention);
    this.interventionStore.getAllPendingReviewingInterventions({ interventionType: paramGuidanceType });
    return this.pendingReviewingInterventions$.pipe(
      skip(1),
      take(1),
      mergeMap((interventions: InterventionInterface[]) => {
        this.initialInterventions = interventions;
        if (paramGuidanceType === undefined) {
          this.emitHasInterventions.emit(interventions.length > 0);
        }
        const pageIndex = this.paginator?.paginator ? this.paginator?.paginator.pageIndex : 0;
        const pageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;
        return this.search(this.filter.searchFilter).pipe(
          mergeMap(() =>
            this.helper.setPagedContent(this.interventionsSubject, this.pagedInterventionsSubject, setFirst, pageIndex, pageSize),
          ),
        );
      }),
    );
  }

  public updateList(): void {
    this.subscriptions.push(this.reloadInterventionList().subscribe());
  }

  public getDetailedIntervention(id: number): void {
    this.router.navigateByUrl(`/interventions/${id}`);
  }

  public updatePagedInterventions(event: InterventionInterface[]) {
    if (event) {
      this.pagedInterventionsSubject.next(event);
    }
  }

  public openDialogInterventionTestPublish(intervention: InterventionInterface): void {
    this.helperDialog
      .openDialogInterventionTestPublish(intervention)
      .afterClosed()
      .pipe(mergeMap(() => this.reloadInterventionList()))
      .subscribe(() => {});
  }

  public openDialogInterventionOfficialPublish(intervention: InterventionInterface): void {
    this.helperDialog
      .openDialogInterventionOfficialPublish(intervention)
      .afterClosed()
      .pipe(mergeMap(() => this.reloadInterventionList()))
      .subscribe(() => {});
  }

  // Reads search value and runs searchFilter
  private search(name: string): Observable<boolean> {
    this.searchText$.next(name);
    return this.searchText$.pipe(
      distinctUntilChanged(),
      mergeMap(() => {
        const filter = this.filter.searchFilter.toLowerCase().trim();
        this.interventionsSubject.next(
          this.helper.filterInterventionsBy(this.initialInterventions, filter),
        );
        return of(true);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
